import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { FiLogOut, FiTrash2 } from 'react-icons/fi';

const DashboardCliente = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchBookings();
  }, [user, navigate]);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/appointments/my-bookings`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setBookings(response.data.bookings || []);
      setError(null);
    } catch (err) {
      console.error('Errore caricamento prenotazioni:', err);
      setError('Errore nel caricamento delle prenotazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleCancelBooking = async (id) => {
    if (!window.confirm('Vuoi annullare questa prenotazione?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/appointments/cancel/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      // Aggiorna la lista localmente
      setBookings(bookings.filter(b => b.id !== id));
      // Facoltativo: refetch per sincronizzazione completa
      await fetchBookings();
    } catch (err) {
      setError('Errore nell\'annullamento della prenotazione');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-green-600 to-green-700 text-white py-6 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Dashboard Cliente</h1>
            <p className="text-green-100">{user?.companyName}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg transition"
          >
            <FiLogOut size={20} />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-12">
        {/* CTA Buttons */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <button
            onClick={() => navigate('/prenota-carico')}
            className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl hover:scale-105 transition text-left group"
          >
            <div className="text-5xl mb-4">📦</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Prenota Carico</h2>
            <p className="text-gray-600">Prenota un carico di materiale presso la struttura</p>
          </button>

          <button
            onClick={() => navigate('/prenota-scarico')}
            className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl hover:scale-105 transition text-left group"
          >
            <div className="text-5xl mb-4">📥</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Prenota Scarico</h2>
            <p className="text-gray-600">Prenota uno scarico di materiale presso la struttura</p>
          </button>
        </div>

        {/* Prenotazioni */}
        <section className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Storico Prenotazioni</h2>

          {loading && <p className="text-gray-600">Caricamento prenotazioni...</p>}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {!loading && bookings.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600">Nessuna prenotazione al momento</p>
            </div>
          )}

          {!loading && bookings.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 border-b-2 border-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-left text-gray-700 font-semibold">Giorno</th>
                    <th className="px-4 py-3 text-left text-gray-700 font-semibold">Creata il</th>
                    <th className="px-4 py-3 text-left text-gray-700 font-semibold">Ora</th>
                    <th className="px-4 py-3 text-left text-gray-700 font-semibold">Tipo</th>
                    <th className="px-4 py-3 text-left text-gray-700 font-semibold">Materiale</th>
                    <th className="px-4 py-3 text-left text-gray-700 font-semibold">Mittente</th>
                    <th className="px-4 py-3 text-left text-gray-700 font-semibold">Destinatario</th>
                    <th className="px-4 py-3 text-left text-gray-700 font-semibold">Stato</th>
                    <th className="px-4 py-3 text-center text-gray-700 font-semibold">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id} className="even:bg-gray-50">
                      <td className="px-4 py-3">{formatDate(booking.date)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(booking.createdAt).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                        <br />
                        <span className="text-xs text-gray-500">
                          {new Date(booking.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </td>
                      <td className="px-4 py-3">{booking.timeSlot}</td>
                      <td className="px-4 py-3">
                        <span className={`px-3 py-1 rounded text-sm font-medium ${
                          booking.type === 'carico' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {booking.type === 'carico' ? '📦 Carico' : '📥 Scarico'}
                        </span>
                      </td>
                      <td className="px-4 py-3">{booking.material}</td>
                      <td className="px-4 py-3">{booking.origin}</td>
                      <td className="px-4 py-3">{booking.destination}</td>
                      <td className="px-4 py-3">
                        <span className={`px-3 py-1 rounded text-sm font-medium ${
                          booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {booking.status === 'confirmed' ? '✓ Confermato' :
                           booking.status === 'pending' ? '⏳ In attesa' :
                           booking.status === 'completed' ? '✓ Completato' :
                           '✗ Annullato'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {booking.status !== 'completed' && booking.status !== 'cancelled' && (
                          <button
                            onClick={() => handleCancelBooking(booking.id)}
                            className="text-red-600 hover:text-red-800 transition"
                            title="Annulla prenotazione"
                          >
                            <FiTrash2 size={20} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default DashboardCliente;
